import {
  useEffect,
  useState,
} from "react";
import axios from "axios";
import {
  FaBatteryFull,
  FaCar,
  FaClock,
  FaMapMarkerAlt,
  FaRoad,
  FaSatelliteDish,
  FaSignal,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://interventi.synology.me:5050";

function getConfig() {
  const token =
    localStorage.getItem("token");

  return {
    headers: token
      ? {
          Authorization:
            `Bearer ${token}`,
        }
      : {},
  };
}

function num(
  value,
  unit = "",
  decimals = 1
) {
  const n = Number(value);

  if (!Number.isFinite(n)) {
    return "—";
  }

  return `${n.toFixed(decimals)}${unit}`;
}

function yesNo(value) {
  if (value === true) {
    return "Sì";
  }

  if (value === false) {
    return "No";
  }

  return "—";
}

function Capability({
  label,
  available,
}) {
  return (
    <div
      className={`flotta-health-capability ${
        available
          ? "available"
          : "missing"
      }`}
    >
      <strong>{label}</strong>
      <span>
        {available
          ? "Disponibile"
          : "Non ricevuto"}
      </span>
    </div>
  );
}

export default function FlottaSalute() {
  const [data, setData] =
    useState(null);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState("");

  const load = async ({
    spinner = false,
  } = {}) => {
    if (spinner) {
      setLoading(true);
    }

    try {
      const response =
        await axios.get(
          `${API_BASE_URL}/api/flotta/health`,
          getConfig()
        );

      setData(response.data);
      setError("");
    } catch (err) {
      console.error(
        "Errore salute veicolo:",
        err
      );

      setError(
        "Salute veicolo non disponibile"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({ spinner: true });

    const timer = setInterval(
      () => load(),
      15000
    );

    return () =>
      clearInterval(timer);
  }, []);

  if (loading && !data) {
    return (
      <div className="flotta-health-loading">
        Caricamento salute veicolo...
      </div>
    );
  }

  const tracker =
    data?.tracker || {};
  const week =
    data?.sevenDays || {};
  const caps =
    data?.capabilities || {};

  return (
    <section className="flotta-health-page">
      <div className="flotta-health-title">
        <div>
          <h3>
            Salute veicolo e tracker
          </h3>
          <p>
            FMC920 · alimentazione · GNSS ·
            GSM · percorrenza
          </p>
        </div>

        <div className="flotta-health-actions">
          <span
            className={`flotta-health-status ${
              tracker.online
                ? "online"
                : "offline"
            }`}
          >
            {tracker.online
              ? "FMC920 online"
              : "FMC920 non recente"}
          </span>

          <button
            type="button"
            onClick={() =>
              load({
                spinner: true,
              })
            }
          >
            <FaSyncAlt /> Aggiorna
          </button>
        </div>
      </div>

      {error && (
        <div className="flotta-health-warning">
          {error}
        </div>
      )}

      <div className="flotta-health-kpis">
        <div>
          <FaCar />
          <strong>
            Alimentazione veicolo
          </strong>
          <span>
            {num(
              tracker.externalVoltageV,
              " V",
              2
            )}
          </span>
          <small>
            7 gg{" "}
            {num(
              week.externalVoltageMinV,
              "",
              2
            )}
            {" – "}
            {num(
              week.externalVoltageMaxV,
              " V",
              2
            )}
          </small>
        </div>

        <div>
          <FaBatteryFull />
          <strong>
            Batteria FMC920
          </strong>
          <span>
            {num(
              tracker.internalBatteryV,
              " V",
              3
            )}
          </span>
          <small>
            corrente{" "}
            {num(
              tracker.internalBatteryCurrentMa,
              " mA",
              0
            )}
          </small>
        </div>

        <div>
          <FaRoad />
          <strong>
            Odometro tracker
          </strong>
          <span>
            {num(
              tracker.odometerTrackerKm,
              " km",
              1
            )}
          </span>
          <small>
            non è il contachilometri OEM
          </small>
        </div>

        <div>
          <FaClock />
          <strong>
            Ore tracker
          </strong>
          <span>
            {num(
              tracker.engineHoursTracker,
              " h",
              1
            )}
          </span>
          <small>
            7 gg{" "}
            {num(
              week.engineHours,
              " h",
              1
            )}
          </small>
        </div>
      </div>

      <div className="flotta-health-section">
        <h4>Connettività e posizione</h4>

        <div className="flotta-health-grid">
          <div>
            <FaSignal />
            <strong>Segnale GSM</strong>
            <span>
              {tracker.gsmSignal ===
                null ||
              tracker.gsmSignal ===
                undefined
                ? "—"
                : `${tracker.gsmSignal}/5`}
            </span>
          </div>

          <div>
            <FaSatelliteDish />
            <strong>GNSS</strong>
            <span>
              {tracker.gnssLabel ||
                "—"}
            </span>
          </div>

          <div>
            <FaMapMarkerAlt />
            <strong>Satelliti</strong>
            <span>
              {tracker.satellites ??
                "—"}
            </span>
          </div>

          <div>
            <strong>HDOP / PDOP</strong>
            <span>
              {num(
                tracker.hdop,
                "",
                1
              )}
              {" / "}
              {num(
                tracker.pdop,
                "",
                1
              )}
            </span>
          </div>

          <div>
            <strong>Sleep mode</strong>
            <span>
              {tracker.sleepLabel ||
                "—"}
            </span>
          </div>

          <div>
            <strong>Quadro / movimento</strong>
            <span>
              {yesNo(
                tracker.ignition
              )}
              {" / "}
              {yesNo(
                tracker.motion
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="flotta-health-section">
        <h4>Ultimi 7 giorni</h4>

        <div className="flotta-health-week">
          <div>
            <strong>Percorrenza</strong>
            <span>
              {num(
                week.distanceKm,
                " km",
                1
              )}
            </span>
          </div>

          <div>
            <strong>Viaggi</strong>
            <span>
              {week.trips ?? "—"}
            </span>
          </div>

          <div>
            <strong>Durata viaggi</strong>
            <span>
              {num(
                week.tripDurationHours,
                " h",
                1
              )}
            </span>
          </div>

          <div>
            <strong>Accensioni</strong>
            <span>
              {week.ignitionOn ??
                "—"}
            </span>
          </div>

          <div>
            <strong>Geofence IN</strong>
            <span>
              {week.geofenceEnter ??
                "—"}
            </span>
          </div>

          <div>
            <strong>Geofence OUT</strong>
            <span>
              {week.geofenceExit ??
                "—"}
            </span>
          </div>
        </div>
      </div>

      <div className="flotta-health-section">
        <h4>
          Dati OBD aggiuntivi
        </h4>

        <p className="flotta-health-note">
          Presenza rilevata nei pacchetti AVL
          degli ultimi 7 giorni.
        </p>

        <div className="flotta-health-capabilities">
          <Capability
            label="Temperatura aria aspirata"
            available={
              caps.obdIntakeAirTemp
            }
          />
          <Capability
            label="Temperatura olio"
            available={
              caps.obdOilTemp
            }
          />
          <Capability
            label="Fuel rate"
            available={
              caps.obdFuelRate
            }
          />
          <Capability
            label="VIN"
            available={caps.obdVin}
          />
          <Capability
            label="Codici guasto Pxxxx"
            available={
              caps.obdFaultCodes
            }
          />
        </div>
      </div>
    </section>
  );
}
